import { err, ok } from '@src/services/common/Result';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { pickNextSavedSegmentColor } from '@src/utility/savedSegmentColors';

import type { Result } from '@src/services/common/Result';
import type { DatoClientStore } from '@src/stores-v2/DatoClientStore';
import type { Story } from '@src/types.ts/story';

type SavedSegments = NonNullable<Story['savedSegments']>;

/**
 * Narrow port over the observable story state the service needs. The concrete
 * VideoCreatorStore satisfies it structurally, so the service never depends on
 * the god-object store type. The service stays MobX-free: the optimistic write
 * goes through `setSavedSegments`, which the store implements as a plain
 * observable assignment.
 */
export interface SavedSegmentStore {
  readonly story?: Pick<Story, 'id' | 'savedSegments'>;
  readonly savedSegments: SavedSegments;
  setSavedSegments(next: SavedSegments): void;
}

export interface SavedSegmentServiceDeps {
  datoClientStore: DatoClientStore;
}

export interface AddSavedSegmentInput {
  name: string;
  startIndex: number;
  endIndex: number;
  /** Editor display name recorded on the group (from UserIdentityStore). */
  username: string;
  /** Optional explicit color; falls back to the next distinct palette entry. */
  color?: string;
}

/**
 * Persistence for the save-for-later → combine flow's named highlight groups
 * (`story.savedSegments`). Each mutation optimistically updates the store's
 * observable via the injected port, writes the single `saved_segments` field to
 * Dato through the story repository, and reverts the optimistic change on
 * failure. Mirrors SavePullQuoteService: narrow `{ datoClientStore }` deps,
 * Result returns, no MobX.
 */
export class SavedSegmentService {
  private readonly _datoClientStore: DatoClientStore;
  private readonly _logger = LogManager.getLogger(
    'service:SavedSegmentService',
  );

  constructor(deps: SavedSegmentServiceDeps) {
    this._datoClientStore = deps.datoClientStore;
  }

  /**
   * Create a new named group seeded with one highlight range. Returns the new
   * group's key (epoch ms) on success.
   */
  async addSegment(
    store: SavedSegmentStore,
    input: AddSavedSegmentInput,
  ): Promise<Result<number>> {
    if (!store.story) {
      return err('PRECONDITION', 'addSegment: story is not defined');
    }
    const previous = store.savedSegments;
    const key = new Date().getTime();
    // The creator picks the color up front (context-menu swatches); fall back
    // to the next distinct palette entry for callers that don't specify one.
    const color =
      input.color ??
      pickNextSavedSegmentColor(
        Object.values(previous)
          .map(group => group.color)
          .filter((c): c is string => !!c),
      );
    const next: SavedSegments = {
      ...previous,
      [key]: {
        name: input.name,
        ranges: [{ startIndex: input.startIndex, endIndex: input.endIndex }],
        color,
        username: input.username,
        createdAt: key,
      },
    };
    const result = await this._persist(store, next, previous, 'addSegment');
    return result.ok ? ok(key) : result;
  }

  /** Append a highlight range to an existing group ("Save to {name}"). */
  async appendRange(
    store: SavedSegmentStore,
    key: number,
    range: { startIndex: number; endIndex: number },
  ): Promise<Result<void>> {
    const previous = store.savedSegments;
    const group = previous[key];
    if (!group) {
      return err('NOT_FOUND', 'appendRange: group not found', { key });
    }
    const next: SavedSegments = {
      ...previous,
      [key]: { ...group, ranges: [...group.ranges, range] },
    };
    return this._persist(store, next, previous, 'appendRange');
  }

  /** Rename a group. */
  async renameSegment(
    store: SavedSegmentStore,
    key: number,
    name: string,
  ): Promise<Result<void>> {
    const previous = store.savedSegments;
    const group = previous[key];
    if (!group) {
      return err('NOT_FOUND', 'renameSegment: group not found', { key });
    }
    const next: SavedSegments = { ...previous, [key]: { ...group, name } };
    return this._persist(store, next, previous, 'renameSegment');
  }

  /** Remove a group by its key. */
  async removeSegment(
    store: SavedSegmentStore,
    key: number,
  ): Promise<Result<void>> {
    const previous = store.savedSegments;
    if (!previous[key]) {
      return err('NOT_FOUND', 'removeSegment: group not found', { key });
    }
    const next: SavedSegments = { ...previous };
    delete next[key];
    return this._persist(store, next, previous, 'removeSegment');
  }

  // Optimistically apply `next` via the port, persist to Dato, and revert to
  // `previous` on failure. All logic lives here; only the observable write
  // crosses back into the store through `setSavedSegments`.
  private async _persist(
    store: SavedSegmentStore,
    next: SavedSegments,
    previous: SavedSegments,
    context: string,
  ): Promise<Result<void>> {
    if (!store.story) {
      return err('PRECONDITION', `${context}: story is not defined`);
    }
    store.setSavedSegments(next);
    try {
      await this._datoClientStore.storyRepository.saveSavedSegments(
        store.story,
        next,
      );
      return ok(undefined);
    } catch (e) {
      store.setSavedSegments(previous);
      this._logger.error(`${context}: failed to persist`, extractErrorInfo(e));
      return err('EXTERNAL', `${context}: failed to persist`, e);
    }
  }
}

export default SavedSegmentService;
